import {
  Button,
  Input,
  Spinner,
  Text,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react"
import {FormBox} from "components/auth-forms/form-box"
import {FormHeading} from "components/auth-forms/form-heading"
import {FormMessage} from "components/auth-forms/form-message"
import {FormScreen} from "components/auth-forms/form-screen"
import Link from "next/link"
import {post} from "plugins/api/helpers"
import {FC, FormEvent, useEffect, useState} from "react"

export const SignupScreen: FC<{}> = () => {
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")

  const [status, setStatus] = useState<
    {type: "loading"} | {type: "success"} | {type: "error"; error: string}
  >()

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const queryEmail = params.get("email")

    if (queryEmail && !email) {
      setEmail(queryEmail)
    }
  }, [])

  const submit = async (ev: FormEvent) => {
    ev.preventDefault()

    if (!email || !password || !confirmPassword) {
      setStatus({type: "error", error: "Please fill out all fields"})
      return
    }

    if (password !== confirmPassword) {
      setStatus({type: "error", error: "Passwords do not match"})
      return
    }

    setStatus({type: "loading"})

    const response = await post("/api/users/signup", {
      data: {email, password, firstName},
    })
    const data = await response.json()

    if (data.success) {
      setStatus({type: "success"})
    } else {
      setStatus({type: "error", error: data.error ?? "Uknown error"})
    }
  }

  const inputSize = useBreakpointValue(["lg", "md"])
  const loading = status?.type === "loading"

  return (
    <FormScreen>
      <FormBox>
        <FormHeading>✍️ Sign up for FocusTask</FormHeading>

        {status?.type === "success" ? (
          <VStack spacing={6}>
            <Text>
              Your account was created! Please check your inbox (and SPAM) for a
              verification link. We sent it to{" "}
              <strong className="rounded-md bg-yellow-100 px-1 font-medium dark:bg-yellow-800">
                {email}
              </strong>
              .
            </Text>

            <Link href="/" passHref>
              <Button width="100%" colorScheme="blue">
                Go back
              </Button>
            </Link>
          </VStack>
        ) : (
          <form onSubmit={submit}>
            <div className="flex flex-col items-stretch space-y-4">
              <p>
                Welcome, we're glad to have you. Keep in mind FocusTask is in
                beta, and we're still changing things.
              </p>

              {status?.type === "error" ? (
                <FormMessage status="error">{status.error}</FormMessage>
              ) : null}

              <div className="space-y-1">
                <Input
                  size={inputSize}
                  placeholder="First name"
                  value={firstName}
                  onChange={(ev) => setFirstName(ev.target.value)}
                  disabled={loading}
                />

                <Input
                  size={inputSize}
                  placeholder="E-mail"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  type="email"
                  disabled={loading}
                  // enterKeyHint="go"
                />

                <Input
                  size={inputSize}
                  placeholder="Password"
                  type="password"
                  value={password}
                  onChange={(ev) => setPassword(ev.target.value)}
                  disabled={loading}
                  // enterKeyHint="go"
                />

                <Input
                  size={inputSize}
                  placeholder="Confirm password"
                  type="password"
                  value={confirmPassword}
                  onChange={(ev) => setConfirmPassword(ev.target.value)}
                  enterKeyHint="go"
                  disabled={loading}
                />
              </div>

              <div className="flex flex-col items-stretch space-y-1">
                <Button
                  type="submit"
                  size={inputSize}
                  width="100%"
                  colorScheme="blue"
                  isDisabled={status?.type === "loading"}
                >
                  {status?.type === "loading" ? (
                    <Spinner size="sm" mr={2} />
                  ) : null}
                  Sign up
                </Button>

                <Link href="/" passHref>
                  <Button type="submit" size={inputSize} width="100%">
                    Cancel
                  </Button>
                </Link>
              </div>
            </div>
          </form>
        )}
      </FormBox>
    </FormScreen>
  )
}
